import React, { useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { Button } from "./ui/button";
import { Menu, X, LogOut } from "lucide-react";
import { useAuth } from "../contexts/AuthContext";

const Header = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const location = useLocation();
  const { isAuthenticated, logout } = useAuth();

  const navLinks = [
    { name: "Solutions", href: "/#solutions" },
    { name: "Why Us", href: "/#why-us" },
    { name: "Blog", href: "/#blog" },
    { name: "Contact Us", href: "/#contact" }
  ];

  const isActive = (href) => {
    if (href.startsWith("/#")) return location.pathname === "/" && location.hash === href.substring(1);
    return location.pathname === href;
  };

  const handleNavClick = (href, e) => {
    if (href === "/") {
      e.preventDefault();
      // If we're not on the home page, navigate there first
      if (location.pathname !== "/") {
        window.location.href = "/";
        return;
      }
      // If we're on the home page, scroll to top
      window.scrollTo({ top: 0, behavior: 'smooth' });
      window.history.pushState(null, null, "/");
    } else if (href.startsWith("/#")) {
      e.preventDefault();
      const sectionId = href.substring(2); // Remove "/#"

      // If we're not on the home page, navigate there first
      if (location.pathname !== "/") {
        window.location.href = href;
        return;
      }

      // If we're on the home page, scroll to the section
      const element = document.getElementById(sectionId);
      if (element) {
        element.scrollIntoView({ behavior: 'smooth' });
        // Update the URL hash
        window.history.pushState(null, null, href);
      }
    }
  };

  const handleBetaRequest = () => {
    // First navigate to beta section
    if (location.pathname !== "/") {
      window.location.href = "/#beta-program";
      return;
    }

    // Scroll to beta section
    const element = document.getElementById('beta-program');
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
      window.history.pushState(null, null, "/#beta-program");
    }

    // Trigger the beta request modal/action after a short delay
    setTimeout(() => {
      const betaButton = document.querySelector('[data-beta-request-button]');
      if (betaButton) {
        betaButton.click();
      }
    }, 500);
  };

  return (
    <header className="bg-white/95 backdrop-blur-sm border-b border-slate-200 sticky top-0 z-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center py-4">
          {/* Logo */}
          <Link to="/" onClick={(e) => handleNavClick("/", e)} className="flex items-center space-x-3">
            <img
              src="/images/myfinbrain-logo.svg"
              alt="MyFinBrain Logo"
              className="w-12 h-12"
            />
            <span className="text-2xl font-bold text-slate-800">MyFinBrain</span>
          </Link>

          {/* Desktop Navigation */}
          <nav className="hidden md:flex items-center space-x-8">
            {navLinks.map((link) => (
              <Link
                key={link.name}
                to={link.href}
                onClick={(e) => handleNavClick(link.href, e)}
                className={`text-sm font-medium transition-colors duration-200 ${
                  isActive(link.href)
                    ? "text-blue-600"
                    : "text-slate-600 hover:text-slate-900"
                }`}
              >
                {link.name}
              </Link>
            ))}
          </nav>

          {/* CTA Button and Auth */}
          <div className="hidden md:flex items-center gap-3">
            <Button
              className="bg-blue-600 hover:bg-blue-700 text-white px-6 py-2 transition-all duration-200 shadow-md hover:shadow-lg"
              onClick={handleBetaRequest}
            >
              Request Beta Access
            </Button>
            {isAuthenticated && (
              <Button
                variant="outline"
                size="sm"
                onClick={logout}
                className="flex items-center gap-2"
              >
                <LogOut size={16} />
                Logout
              </Button>
            )}
          </div>

          {/* Mobile Menu Button */}
          <button
            className="md:hidden p-2 rounded-md text-slate-600 hover:text-slate-900 hover:bg-slate-100 transition-colors duration-200"
            onClick={() => setIsMenuOpen(!isMenuOpen)}
          >
            {isMenuOpen ? <X size={24} /> : <Menu size={24} />}
          </button>
        </div>

        {/* Mobile Menu */}
        {isMenuOpen && (
          <div className="md:hidden py-4 border-t border-slate-200">
            <nav className="flex flex-col space-y-4">
              {navLinks.map((link) => (
                <Link
                  key={link.name}
                  to={link.href}
                  className={`text-sm font-medium transition-colors duration-200 ${
                    isActive(link.href)
                      ? "text-blue-600"
                      : "text-slate-600 hover:text-slate-900"
                  }`}
                  onClick={(e) => {
                    handleNavClick(link.href, e);
                    setIsMenuOpen(false);
                  }}
                >
                  {link.name}
                </Link>
              ))}
              <Button
                className="bg-blue-600 hover:bg-blue-700 text-white w-fit px-6 py-2 mt-4 transition-all duration-200"
                onClick={() => {
                  setIsMenuOpen(false);
                  handleBetaRequest();
                }}
              >
                Request Beta Access
              </Button>
              {isAuthenticated && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => {
                    setIsMenuOpen(false);
                    logout();
                  }}
                  className="flex items-center gap-2 mt-2 w-fit"
                >
                  <LogOut size={16} />
                  Logout
                </Button>
              )}
            </nav>
          </div>
        )}
      </div>
    </header>
  );
};

export default Header;